import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DocumentViewer } from '../../components';
import './SocialCounseling.css';

const SocialCounseling = () => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState('');
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [isWord, setIsWord] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      const localeMap = {
        'en': 'en',
        'en-US': 'en',
        'cz': 'cz',
        'cs': 'cz',
        'cs-CZ': 'cz',
        'de': 'de',
        'de-DE': 'de'
      };

      const locale = localeMap[i18n.language] || 'en';
      try {
        const response = await fetch(`/social_counseling/social_counseling_${locale}.md`);
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error('Error:', error);
        try {
          const response = await fetch('/social_counseling/social_counseling_en.md');
          const text = await response.text();
          setContent(text);
        } catch (err) {
          console.error('Error:', err);
        }
      }
    };

    loadContent();
  }, [i18n.language]);

  useEffect(() => {
    return () => {
      if (docUrl) {
        URL.revokeObjectURL(docUrl);
      }
    };
  }, [docUrl]);

  const documents = [
    {
      name: 'vnitrni pravidla pro po skytování služby osp.doc',
      title: t('socialCounseling.doc1', 'Internal Rules for Service Provision')
    },
    {
      name: 'informační leták.doc',
      title: t('socialCounseling.doc2', 'Information Leaflet')
    },
    {
      name: 'Pravidla pro podavani a  vyřizování stížností.doc',
      title: t('socialCounseling.doc3', 'Rules for Submitting and Handling Complaints')
    },
    {
      name: 'odpoved_zverejneni.pdf',
      title: t('socialCounseling.doc4', 'Publication Response')
    }
  ];

  const handleViewDocument = async (doc) => {
    try {
      const response = await fetch(`/social_counseling/${doc.name}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setDocUrl(url);
      const isPdfDoc = blob.type === 'application/pdf' || doc.name.toLowerCase().endsWith('.pdf');
      const isWordDoc = blob.type === 'application/msword' ||
        blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        doc.name.toLowerCase().endsWith('.doc') ||
        doc.name.toLowerCase().endsWith('.docx');

      setIsPdf(isPdfDoc);
      setIsWord(isWordDoc);
      setViewingDoc(doc);
    } catch (error) {
      console.error('Failed to load document:', error);
    }
  };

  const handleCloseViewer = () => {
    if (docUrl) {
      URL.revokeObjectURL(docUrl);
    }
    setDocUrl(null);
    setViewingDoc(null);
    setIsPdf(false);
    setIsWord(false);
  };

  return (
    <div className="social-counseling-page">
      <main className="social-counseling-main">
        <div className="social-counseling-container">
          <h1 className="social-counseling-title">
            {t('socialCounseling.title', 'Social Counseling')}
          </h1>

          <div className="social-counseling-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          </div>

          <div className="social-counseling-documents">
            <h2 className="social-counseling-documents-title">
              {t('socialCounseling.documentsTitle', 'Related Documents')}
            </h2>
            <div className="social-counseling-documents-grid">
              {documents.map((doc, index) => (
                <div
                  key={index}
                  className="social-counseling-document-card"
                >
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6M9 16h6" />
                  </svg>
                  <span className="social-counseling-document-title">{doc.title}</span>
                  <div className="social-counseling-document-actions">
                    <button
                      className="social-counseling-document-btn view-btn"
                      onClick={() => handleViewDocument(doc)}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                    <a
                      href={`/social_counseling/${doc.name}`}
                      download
                      className="social-counseling-document-btn download-btn"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {viewingDoc && docUrl && (
            <div className="social-counseling-document-viewer-overlay" onClick={handleCloseViewer}>
              <div className="social-counseling-document-viewer-container" onClick={(e) => e.stopPropagation()}>
                <div className="social-counseling-document-viewer-header">
                  <h3>{viewingDoc.title}</h3>
                  <button className="social-counseling-close-btn" onClick={handleCloseViewer}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <DocumentViewer docUrl={docUrl} isPdf={isPdf} isWord={isWord} documentFilename={viewingDoc.name} />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SocialCounseling;
